import { AdminClient } from '@storefronts/common-types'
import { useRouter } from 'next/router'
import { usePostHog } from 'posthog-js/react'
import { useContext, useEffect, useState } from 'react'
import { themes } from '../../styles/Theme'
import { isInternalUser } from '@storefronts/common-types'
import { R } from '../modules/api/actions'
import { AuthContext } from '../modules/auth/authContext'
import { processClientHashFromUrl } from '../modules/auth/processClientHashFromUrl'
import { unprotectedRoutes } from '../modules/auth/unprotectedRoutes'
import { SideNavigation } from './navigation/SideNavigation'
import { BrandedLoadingScreen } from './shared/BrandedLoadingScreen'
import { AppShellProvider } from './shared/AppShellContext'
import { TopNavigation } from './navigation/TopNavigation'
import { useIsomorphicLayoutEffect } from '@/hooks/useIsomorphicLayoutEffect'
import { LocalStorageKey } from '@/lib/storageConstants'
import { toast } from '@/components/ui/sonner'
import { cn } from '@/lib/utils'

export const OnboardingLayout: React.FunctionComponent<{
    children: React.ReactNode
}> = ({ children }) => {
    const router = useRouter()
    const authContext = useContext(AuthContext)
    const [isAuthSetup, setIsAuthSetup] = useState(false)
    const [showLoading, setShowLoading] = useState(true)
    const [canMountAppShell, setCanMountAppShell] = useState(false)
    const posthog = usePostHog()

    useIsomorphicLayoutEffect(() => {
        const clientHash = window.localStorage.getItem(
            LocalStorageKey.CLIENT_HASH,
        )
        if (clientHash) {
            R.setClient(clientHash)
            setCanMountAppShell(true)
        }
    }, [])

    useEffect(() => {
        if (!router.isReady) {
            return
        }
        // make profile request
        // TODO - how to share this duplicate code with AuthLayout
        R.getProfile()
            .then(async (resp) => {
                if (resp.isAuthenticated) {
                    // TODO: is this necessary? R.getProfile already sets the client
                    if (resp.user.client) {
                        R.setClient(resp.user.client.clientHash)
                        setCanMountAppShell(true)
                    }
                    authContext.setLoggedIn(true)
                    authContext.setUser(resp.user)
                    if (isInternalUser(resp.user.username)) {
                        authContext.setIsSuperUser(true)
                    }
                    const clientInLocalStorage = window.localStorage.getItem(
                        LocalStorageKey.CLIENT_HASH,
                    )

                    let client: AdminClient
                    if (
                        resp.user.client &&
                        (!clientInLocalStorage ||
                            clientInLocalStorage ===
                                resp.user.client.clientHash)
                    ) {
                        // If no stored client or stored client is the user's primary client
                        client = resp.user.client
                    }

                    if (resp.user.client) {
                        // If persisted client is one of user's other clients
                        const allClientsResp = await R.getAvailableClients()
                        const allClients = allClientsResp.clients
                        const filteredClient = allClients.find(
                            (c) => c.clientHash === clientInLocalStorage,
                        )

                        if (filteredClient) {
                            // If persisted client is one of user's other clients
                            client = filteredClient
                        } else {
                            //Fallback
                            client = resp.user.client
                        }

                        // Shopify app install redirect: switch to client from URL if user has access
                        const { client: clientAfterUrl, accessDenied } =
                            processClientHashFromUrl({
                                query: router.query,
                                pathname: router.pathname,
                                replace: router.replace,
                                allClients,
                                currentClient: client,
                            })
                        client = clientAfterUrl
                        if (accessDenied) {
                            toast.error('No access', {
                                description:
                                    "You don't have access to this client.",
                            })
                        }

                        window.localStorage.setItem(
                            LocalStorageKey.CLIENT_HASH,
                            client?.clientHash,
                        )
                        authContext.setClient(client)
                        R.setClient(client?.clientHash)

                        // Identify posthog
                        posthog.identify(resp.user.username, {
                            username: resp.user.username,
                            client: client?.name,
                        })
                    }

                    setIsAuthSetup(true)
                    return
                }

                if (
                    !unprotectedRoutes.includes(router.pathname) &&
                    (authContext.isLoggedIn === null ||
                        authContext.user === null)
                ) {
                    router.push('/login')
                }
            })
            .catch((err) => {
                console.error(err)
                router.push('/login')
                toast.warning('Unable to log you in.', {
                    description: '',
                })
            })
    }, [router.isReady]) // eslint-disable-line react-hooks/exhaustive-deps

    const isAuthReady = !!authContext.isLoggedIn && isAuthSetup
    const shouldMountApp = canMountAppShell || isAuthSetup

    const blurSideNavigation =
        router.pathname === '/onboarding' &&
        (router.query.step === 'activate-trial' ||
            router.query.step === 'checkout-completed' ||
            router.query.step === 'restart-subscription' ||
            router.query.step === 'book-call')

    return (
        <div
            className="relative flex min-h-screen w-screen flex-col bg-background"
            style={{
                fontFamily: themes.fonts.sans,
            }}
        >
            {shouldMountApp ? (
                <AppShellProvider isShellVisible={!showLoading}>
                    <div
                        className={cn(showLoading && 'invisible')}
                        aria-hidden={showLoading}
                    >
                        <TopNavigation />
                        <div className="relative flex w-full pt-[64px]">
                            {blurSideNavigation ? (
                                <div className="fixed bottom-0 left-0 top-[64px] flex w-[240px] opacity-80">
                                    <SideNavigation collapseEnabled={false} />
                                </div>
                            ) : null}
                            <div
                                className={cn(
                                    'flex flex-[5] flex-col overflow-auto bg-background p-4',
                                    blurSideNavigation ? 'pl-[240px]' : 'pl-0',
                                )}
                                style={{
                                    maxHeight: 'calc(100vh - 64px)',
                                    height: 'calc(100vh - 64px)',
                                }}
                            >
                                {children}
                            </div>
                        </div>
                    </div>
                </AppShellProvider>
            ) : null}
            {showLoading && (
                <BrandedLoadingScreen
                    overlay
                    isReady={isAuthReady}
                    onFadeComplete={() => setShowLoading(false)}
                />
            )}
        </div>
    )
}
